/**
 * ReviewPrompt — the review-flywheel UI (PROMPT-52).
 *
 * One shared, non-blocking card for BOTH the web app and the extension side
 * panel. It listens for the `persona:review-show` event that reviewFlywheel
 * .recordUse() dispatches after the gating conditions are met, so it can be
 * mounted once at each app root without threading state through AIMail.
 *
 * Two steps:
 *   1. sentiment — five gold stars (≥4 → happy branch, ≤3 → private feedback)
 *   2a. happy    — optional note + route to the Chrome Web Store (copy ONLY the
 *                  user's own words; web also captures a quotable testimonial)
 *   2b. feedback — required note straight to the team, no public-review push
 *
 * Visual: gold-tinted card, true-black base, gold accents (PROMPT-51 tokens).
 */
import { useEffect, useRef, useState } from 'react';
import {
  RF, SHOW_EVENT, IS_EXTENSION,
  onShow, markTerminalPositive, markGaveFeedback, noteDismiss,
  routeToStore, postFeedback, track, resolveVariant, VARIANT_IDS,
} from '../utils/reviewFlywheel.js';
import Style from './Style.jsx';
import { apiFetch } from '../utils/supabase.js';
import { emitProductEvent } from '../utils/productEvents.js';

// PROMPT-51 palette (hardcoded so the overlay is self-contained in both builds).
const GOLD = '#E8A84A';
const GOLD_DEEP = '#b8882a';
const STAR_LIT = '#E8A84A';
const STAR_UNLIT = '#3a352c';
const T1 = '#f2ede6';
const T2 = '#c4b8a8';
const T3 = '#7a6e62';
const FONT = "'DM Sans', system-ui, -apple-system, sans-serif";
const DISPLAY = "'Fraunces', Georgia, serif";

/**
 * The four A/B arms for the ask itself (L2 deliverable 1).
 *
 * Constraints from the brief, enforced by a test: title <= 6 words, body <= 22
 * words, no exclamation marks, no hype vocabulary. One arm is founder-voiced and
 * is the ONLY place the "one-person project" note is allowed to appear — used
 * once, honestly, and never as leverage.
 *
 * Nothing here offers anything in exchange for a review. That is a Chrome Web
 * Store policy line as well as a brand one, and it is the single hardest rule in
 * this file: an incentivised-review prompt risks delisting.
 *
 * Copy is shared between the web app and the extension deliberately. The old
 * COPY object split on IS_EXTENSION, which would have doubled every arm and made
 * the comparison two underpowered tests instead of one.
 */
const VARIANTS = {
  // Plainest possible ask — the control.
  direct: {
    title: 'Enjoying Persona?',
    sub: 'Tap to rate. Two seconds, and it helps the next person find it.',
    positive: 'Rate on Chrome',
    later: 'Maybe later',
  },
  // Scarcity of social proof, stated as fact rather than urgency.
  early: {
    title: "You're one of the first",
    sub: 'Persona has almost no reviews yet. Yours changes whether anyone else finds it.',
    positive: 'Leave a rating',
    later: 'Not now',
  },
  // Founder-voiced. The one sanctioned use of the small-team note.
  founder: {
    title: 'A small ask from Cristina',
    sub: 'This is a one-person project. An honest rating is the only marketing I cannot do myself.',
    positive: 'Rate on Chrome',
    later: 'Maybe later',
  },
  // Anchors on the value already delivered rather than on the ask.
  specific: {
    title: 'Did it land right?',
    sub: 'If Persona got the tone right this week, a rating on the Chrome Web Store says so.',
    positive: 'Say so on Chrome',
    later: 'Maybe later',
  },
};

/**
 * Share-card lines (L2 deliverable 6).
 *
 * These describe the PRODUCT MOMENT, never the user's draft. Persona's whole
 * promise is that names, emails and phone numbers never leave the browser; a
 * share button that lifts draft text onto social media would contradict the one
 * claim the product is sold on. Nothing here reads `output`.
 *
 * Copied to the clipboard for the user to paste wherever they like. No
 * auto-posting, no pre-filled intent URLs, no network call.
 */
const SHARE_LINES = [
  'Same email, three different tones, one tap. Persona is the first writing tool I have seen do this. #ToneMatters',
  'Found a Gmail add-on that fixes how an email lands, not just the grammar. The "saying no" one earns it alone. #ToneMatters',
  'Persona treats tone like a dial instead of a vibe. The emails I used to dread take about a minute now. #ToneMatters',
];

/**
 * The invite the referrer sends (L2 deliverable 5c) — pre-written, editable,
 * <=80 words, and it has to sound like a person rather than a referral programme.
 *
 * Opened as a mailto: so it lands in THEIR mail client with THEIR name on it.
 * We deliberately do not send it ourselves: mailing an address that never opted
 * in is spam regardless of who suggested it.
 */
export function inviteMailto(url) {
  const subject = 'This writes the emails I hate writing';
  const body = [
    'I have been using this for the emails I put off — the pushback, the no, the',
    'awkward ask. You describe what you actually mean and it drafts it in Gmail,',
    'and you can change the tone until it sounds right.',
    '',
    'If you use my link we both get 7 extra days of the free trial:',
    url,
    '',
    'No card needed to try it.',
  ].join('\n');
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

// Follow-through messages (L2 deliverable 2) — one line, gratitude without
// grovelling. Kept in one place; they were previously duplicated across the
// four finish() call sites.
const DONE = {
  storeCopied: 'Your words are on the clipboard. Paste, post, done — thank you.',
  storePlain: 'The store page is open. A line about what worked is plenty.',
  noteOnly: 'Noted, and appreciated.',
  feedback: 'Got it. Every note gets read.',
};

export default function ReviewPrompt() {
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState('rate');   // rate | happy | feedback | done
  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [note, setNote] = useState('');
  const [allowQuote, setAllowQuote] = useState(true);
  const [doneMsg, setDoneMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [variant, setVariant] = useState(VARIANT_IDS[0]);
  const [refer, setRefer] = useState(null);       // {url, grant, remaining}
  const [shareOffer, setShareOffer] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [inviteCopied, setInviteCopied] = useState(false);
  const closeTimer = useRef(null);

  // Show on the flywheel event; reset to a fresh card each time.
  useEffect(() => {
    const onEvt = () => {
      // Resolve the arm BEFORE the card is shown. Rendering first and patching
      // the variant in afterwards would flash the control copy for a frame and
      // count an impression against an arm the user never actually read.
      // The read is one storage lookup, and the card is already on a 1.5s delay.
      resolveVariant().then((v) => {
        setStep('rate'); setStars(0); setHover(0); setNote('');
        setAllowQuote(true); setDoneMsg(''); setBusy(false);
        setShareOffer(false); setShareCopied(false); setInviteCopied(false);
        setVariant(v);
        setVisible(true);
        onShow(v);   // asksShown++, lastAskAt, fires review_prompt_shown
      // resolveVariant swallows its own storage errors and always resolves, so
      // this only guards against an unexpected throw.
      }).catch(() => {});
    };
    window.addEventListener(SHOW_EVENT, onEvt);
    return () => window.removeEventListener(SHOW_EVENT, onEvt);
  }, []);

  useEffect(() => () => { if (closeTimer.current) clearTimeout(closeTimer.current); }, []);

  if (!visible) return null;

  const src = IS_EXTENSION ? 'extension' : 'web';
  const v = VARIANTS[variant] || VARIANTS.direct;

  const close = () => { if (closeTimer.current) clearTimeout(closeTimer.current); setVisible(false); };
  const dismiss = () => { track('review_dismissed', { source: src, stars, variant }); noteDismiss(); close(); };

  /**
   * Show the closing message. `share` additionally offers a share card and
   * stretches the auto-close: 5.2s is enough to read one line, but not enough to
   * read it, notice a second affordance, and act on it.
   */
  const finish = (msg, { share = false } = {}) => {
    setDoneMsg(msg);
    setShareOffer(share);
    setStep('done');
    closeTimer.current = setTimeout(close, share ? 12000 : 5200);
  };

  // One fixed line per device, so a user who sees the card twice is not handed a
  // different pitch each time. Indexed off the same arm they were bucketed into.
  const shareLine = SHARE_LINES[VARIANT_IDS.indexOf(variant) % SHARE_LINES.length];

  const copyInvite = async () => {
    if (!refer?.url) return;
    if (closeTimer.current) clearTimeout(closeTimer.current);
    try { await navigator.clipboard.writeText(refer.url); } catch { /* label still flips */ }
    setInviteCopied(true);
    track('referral_link_copied', { source: src, surface: 'review_card' });
    emitProductEvent('referral_sent', { extra: { surface: 'review_card' } });
    closeTimer.current = setTimeout(close, 4000);
  };

  const onInviteEmail = () => {
    // The mailto opens their client; nothing is sent by us. Counted the same as a
    // copied link because from the loop's perspective it is the same act.
    if (closeTimer.current) clearTimeout(closeTimer.current);
    track('referral_link_copied', { source: src, surface: 'review_card_email' });
    emitProductEvent('referral_sent', { extra: { surface: 'review_card_email' } });
    closeTimer.current = setTimeout(close, 6000);
  };

  const copyShare = async () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);   // don't close mid-click
    try { await navigator.clipboard.writeText(shareLine); } catch { /* blocked — label still flips */ }
    setShareCopied(true);
    track('share_card_clicked', { source: src, variant });
    closeTimer.current = setTimeout(close, 4000);
  };

  const pickStars = (n) => {
    setStars(n);
    track('review_rated', { stars: n, source: src, variant });
    const happy = n >= RF.POSITIVE_THRESHOLD;
    setStep(happy ? 'happy' : 'feedback');
    // §2.1-E's first goodwill peak. Fetched only on the happy branch: an unhappy
    // user must never be shown a referral ask, and nobody else pays for the call.
    if (happy && !refer) {
      apiFetch('/api/v1/referral/me')
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => { if (d?.url && d.remaining > 0) setRefer(d); })
        .catch(() => {});
    }
  };

  // ── Positive: route to the Chrome Web Store (copy only the user's own note) ──
  const rateOnChrome = async () => {
    if (busy) return; setBusy(true);
    await markTerminalPositive();
    postFeedback({ kind: 'review_intent', stars, comment: note });
    track('review_store_clicked', { source: src });
    // §2.0's named funnel event. Emitted alongside rather than instead of the
    // original so the existing GA4 series stays continuous; this is the one
    // that pairs with review_prompt_shown to give prompt→review conversion,
    // the number §3.1 gates all paid scaling on. `variant` must match the one
    // sent on review_prompt_shown or the per-arm rate is uncomputable.
    track('review_prompt_clicked', { source: src, stars, variant });
    const copied = await routeToStore(note);
    finish(copied ? DONE.storeCopied : DONE.storePlain, { share: true });
  };

  // ── Web positive: capture a (optionally quotable) testimonial ──
  const saveTestimonial = async (alsoStar) => {
    if (busy) return; setBusy(true);
    await markTerminalPositive();
    postFeedback({ kind: 'testimonial', stars, comment: note, allowQuote });
    track('review_testimonial_saved', { source: 'web' });
    if (alsoStar) {
      track('review_store_clicked', { source: 'web' });
      track('review_prompt_clicked', { source: 'web', stars, variant });
      const copied = await routeToStore(note);
      finish(copied ? DONE.storeCopied : DONE.storePlain, { share: true });
    } else {
      // Saved a testimonial but did not go to the store — still a happy user, so
      // the share card is offered here too.
      finish(DONE.noteOnly, { share: true });
    }
  };

  // ── Negative: private feedback straight to the team ──
  const sendFeedback = async () => {
    if (busy || !note.trim()) return; setBusy(true);
    await markGaveFeedback();
    postFeedback({ kind: 'feedback', stars, comment: note });
    track('review_feedback_sent', { source: src, stars, variant });
    // Deliberately NO share offer on the negative path. Asking someone who just
    // told us what is broken to go and promote us is the one move that would
    // turn a recoverable 2-star into a public one.
    finish(DONE.feedback);
  };

  return (
    <div role="dialog" aria-label="Rate Persona" style={cardWrap}>
      <Style>{KEYFRAMES}</Style>
      <div style={card}>
        <button onClick={dismiss} aria-label="Close" style={closeBtn}>×</button>

        {step === 'rate' && (
          <>
            <h3 style={title}>{v.title}</h3>
            <p style={sub}>{v.sub}</p>
            <div role="radiogroup" aria-label="Rate Persona from 1 to 5 stars" style={starRow}>
              {[1, 2, 3, 4, 5].map((n) => {
                const lit = (hover || stars) >= n;
                return (
                  <button
                    key={n}
                    role="radio"
                    aria-checked={stars === n}
                    aria-label={`Rate ${n} star${n > 1 ? 's' : ''}`}
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    onFocus={() => setHover(n)}
                    onBlur={() => setHover(0)}
                    onClick={() => pickStars(n)}
                    style={{ ...starBtn, color: lit ? STAR_LIT : STAR_UNLIT }}
                  >★</button>
                );
              })}
            </div>
          </>
        )}

        {step === 'happy' && (
          <>
            <h3 style={title}>{IS_EXTENSION ? 'Love that.' : 'That means a lot.'}</h3>
            <MiniStars n={stars} />
            <p style={sub}>
              {IS_EXTENSION
                ? "A star on the Chrome Web Store helps more than you'd think — you're one of the first."
                : 'Mind if we feature your words on the site?'}
            </p>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={IS_EXTENSION
                ? "Add a note (optional) — e.g. 'Nails the tone every single time.'"
                : 'Add a note (optional) — what does Persona get right for you?'}
              style={textarea}
              rows={3}
            />
            {!IS_EXTENSION && (
              <label style={checkRow}>
                <input type="checkbox" checked={allowQuote} onChange={(e) => setAllowQuote(e.target.checked)} style={{ accentColor: GOLD }} />
                <span>You can quote me (first name + role) on personadraft.com.</span>
              </label>
            )}
            {IS_EXTENSION ? (
              <>
                <button onClick={rateOnChrome} disabled={busy} style={primaryBtn}>{v.positive} ↗</button>
                <button onClick={dismiss} style={ghostBtn}>{v.later}</button>
              </>
            ) : (
              <>
                <button onClick={() => saveTestimonial(true)} disabled={busy} style={primaryBtn}>{v.positive} ↗</button>
                <button onClick={() => saveTestimonial(false)} disabled={busy} style={ghostBtn}>Just save my note</button>
              </>
            )}
          </>
        )}

        {step === 'feedback' && (
          <>
            <h3 style={title}>Sorry — what&apos;s missing?</h3>
            <p style={sub}>This goes straight to the team. No navigation, no fuss.</p>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Tell us what would make Persona better…"
              style={textarea}
              rows={3}
              autoFocus
            />
            <button onClick={sendFeedback} disabled={busy || !note.trim()} style={{ ...primaryBtn, opacity: note.trim() ? 1 : 0.5 }}>
              Send feedback
            </button>
          </>
        )}

        {step === 'done' && (
          <div style={{ padding: '8px 2px 2px', textAlign: 'center' }}>
            <div style={{ fontSize: 28, marginBottom: 8 }} aria-hidden="true">✦</div>
            <p style={{ ...sub, margin: 0, color: T1 }} aria-live="polite">{doneMsg}</p>
            {shareOffer && refer?.url && (
              <div style={shareBox}>
                <p style={shareLead}>
                  Know someone who dreads the same emails? Invite them and you both
                  get {refer.grant} more.
                </p>
                <button onClick={copyInvite} style={shareBtn} disabled={inviteCopied}>
                  {inviteCopied ? 'Link copied' : 'Copy invite link'}
                </button>
                <a href={inviteMailto(refer.url)} onClick={onInviteEmail} style={inviteLink}>
                  or send it as an email
                </a>
              </div>
            )}
            {shareOffer && !refer?.url && (
              <div style={shareBox}>
                <p style={shareLead}>Know someone who dreads the same emails?</p>
                <p style={shareQuote}>{shareLine}</p>
                <button onClick={copyShare} style={shareBtn} disabled={shareCopied}>
                  {shareCopied ? 'Copied — paste it anywhere' : 'Copy this line'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function MiniStars({ n }) {
  return (
    <div aria-hidden="true" style={{ color: STAR_LIT, fontSize: 16, letterSpacing: 2, marginBottom: 4 }}>
      {'★'.repeat(n)}<span style={{ color: STAR_UNLIT }}>{'★'.repeat(5 - n)}</span>
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const cardWrap = {
  position: 'fixed', right: 16, bottom: 16, zIndex: 2147483000,
  width: 'min(360px, calc(100vw - 24px))', pointerEvents: 'none',
};
const card = {
  pointerEvents: 'auto',
  background: 'linear-gradient(180deg, rgba(232,168,74,.06), rgba(232,168,74,.02)), #0b0b0d',
  border: '1px solid rgba(232,168,74,.3)', borderRadius: 14,
  padding: '20px 18px 18px', position: 'relative', color: T2,
  boxShadow: '0 18px 50px rgba(0,0,0,.5), 0 0 40px rgba(232,168,74,.12)',
  fontFamily: FONT, animation: 'rfIn .28s cubic-bezier(.2,.9,.3,1)',
};
const closeBtn = {
  position: 'absolute', top: 8, right: 10, border: 'none', background: 'transparent',
  color: T3, fontSize: 20, lineHeight: 1, cursor: 'pointer', padding: 4,
};
const title = { margin: '0 0 4px', fontFamily: DISPLAY, fontWeight: 700, fontSize: 18, color: T1 };
const sub = { margin: '0 0 12px', fontSize: 13, lineHeight: 1.5, color: T2 };
const starRow = { display: 'flex', gap: 4, justifyContent: 'center', margin: '6px 0 2px' };
const starBtn = {
  border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 30,
  lineHeight: 1, padding: '2px 4px', transition: 'color .12s, transform .12s',
};
const textarea = {
  width: '100%', boxSizing: 'border-box', resize: 'vertical', minHeight: 60,
  background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.10)',
  borderRadius: 9, padding: '9px 11px', color: T1, fontFamily: FONT, fontSize: 13,
  marginBottom: 10, outline: 'none',
};
const checkRow = {
  display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12, color: T2,
  lineHeight: 1.4, margin: '0 0 12px', cursor: 'pointer',
};
const primaryBtn = {
  width: '100%', padding: '11px 14px', marginBottom: 8, cursor: 'pointer',
  border: 'none', borderRadius: 9, color: '#1a1206', fontWeight: 700, fontSize: 14,
  fontFamily: FONT, background: `linear-gradient(135deg, ${GOLD}, ${GOLD_DEEP})`,
};
// Share card (L2 deliverable 6). Inline objects like the rest of this file —
// the component must be self-contained in both the web and extension bundles.
const shareBox = {
  marginTop: 14, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,.10)',
  textAlign: 'left',
};
const shareLead = { margin: '0 0 8px', fontSize: 12, color: T3, lineHeight: 1.4 };
const shareQuote = {
  margin: '0 0 10px', fontSize: 12, color: T2, lineHeight: 1.5,
  background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.08)',
  borderRadius: 8, padding: '8px 10px',
};
const inviteLink = {
  display: 'block', textAlign: 'center', marginTop: 8, fontSize: 12,
  color: T3, textDecoration: 'underline',
};
const shareBtn = {
  width: '100%', padding: '9px 14px', cursor: 'pointer', fontFamily: FONT,
  border: '1px solid rgba(232,168,74,.35)', borderRadius: 9,
  background: 'rgba(232,168,74,.08)', color: GOLD, fontSize: 13, fontWeight: 600,
};
const ghostBtn = {
  width: '100%', padding: '9px 14px', cursor: 'pointer', fontFamily: FONT,
  border: '1px solid rgba(255,255,255,.14)', borderRadius: 9,
  background: 'transparent', color: T2, fontSize: 13,
};
const KEYFRAMES = `@keyframes rfIn{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
@media (prefers-reduced-motion: reduce){[role=dialog] [style*="rfIn"]{animation:none!important}}`;
